import { useMemo } from 'react';
import { Combobox, type ComboboxOption } from '@/components/combobox';
import { useFxTradeCandidates, type FxTradeCandidate } from '../api/fx-trade-candidates';

export interface FxRefPickerProps {
  value: string | null;
  onChange: (tradeId: string | null) => void;
  // Narrows candidates to the pair being allocated; omit to list all pairs.
  currencyPair?: string | undefined;
  disabled?: boolean | undefined;
  // Defer the network fetch until the picker is actually shown.
  enabled?: boolean | undefined;
  // Accept a hand-typed reference alongside the list. The feed only refreshes
  // on a sync, so a trade executed minutes ago may not be listed yet — without
  // this the desk would be blocked until the next sync.
  allowFreeText?: boolean | undefined;
  className?: string | undefined;
  'aria-label'?: string | undefined;
  id?: string | undefined;
}

const AMOUNT_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

// One scannable line so an operator can match a trade by pair, size, rate and
// date without leaving the picker: "202606…14A · EUR/USD Buy · 1,135,900 @ 1.1359 · 2026-06-25".
function candidateLabel(trade: FxTradeCandidate): string {
  const parts = [trade.tradeId];
  const pairSide = [trade.currencyPair, trade.side].filter(Boolean).join(' ');
  if (pairSide) parts.push(pairSide);
  if (trade.absTradeValue !== null) {
    const amount = AMOUNT_FORMAT.format(Number(trade.absTradeValue));
    parts.push(trade.price !== null ? `${amount} @ ${trade.price}` : amount);
  }
  if (trade.tradeDate !== null) parts.push(trade.tradeDate);
  return parts.join(' · ');
}

// A creatable picker suppresses the shared empty message and only shows the
// add row once something is typed, so zero candidates renders an empty popover
// that reads as broken. This non-selectable row says what to do instead — the
// GBP/USD case, where every broker trade is already attached to an execution.
const TYPE_TO_ENTER_HINT: ComboboxOption = {
  value: '__fx_ref_hint__',
  label: 'No broker trades to choose from — type the reference, or press Fetch.',
  disabled: true,
};

function buildOptions(
  candidates: FxTradeCandidate[] | undefined,
  value: string | null,
  allowFreeText: boolean,
): ComboboxOption[] {
  const list = Array.isArray(candidates) ? candidates : [];
  const options = list.map((trade) => ({
    value: trade.tradeId,
    label: candidateLabel(trade),
  }));
  // Keep a currently-set ref visible even if it's outside the candidate window.
  if (value !== null && !options.some((option) => option.value === value)) {
    return [{ value, label: value }, ...options];
  }
  if (options.length === 0 && allowFreeText) {
    return [TYPE_TO_ENTER_HINT];
  }
  return options;
}

// Replaces the free-text contract-reference input: the operator picks a real
// StoneX broker trade, so the reference is always a valid id (the wrong-typed
// -reference bug can't recur) and the backfill can enrich prompt date + rate.
export function FxRefPicker({
  value,
  onChange,
  currencyPair,
  disabled,
  enabled = true,
  allowFreeText = false,
  className,
  id,
  'aria-label': ariaLabel = 'StoneX broker trade',
}: Readonly<FxRefPickerProps>) {
  const { data, isLoading } = useFxTradeCandidates(currencyPair, enabled);
  const options = useMemo(
    () => buildOptions(data, value, allowFreeText),
    [data, value, allowFreeText],
  );
  const onCreate = allowFreeText
    ? (typed: string) => {
        const trimmed = typed.trim();
        onChange(trimmed === '' ? null : trimmed);
      }
    : undefined;

  return (
    <Combobox
      options={options}
      value={value ?? undefined}
      onChange={(next) => {
        onChange(next ?? null);
      }}
      {...(onCreate ? { onCreate } : {})}
      disabled={disabled}
      loading={isLoading}
      loadingMessage="Loading broker trades…"
      emptyMessage="No matching broker trades."
      placeholder="Select broker trade…"
      searchPlaceholder="Search by ref, amount, date…"
      className={className}
      id={id}
      aria-label={ariaLabel}
    />
  );
}
